import { ApiService } from './api';

export type Campaign = {
  id: number;
  workspaceId: number;
  name: string;
  status: string;
  emailSubject: string | null;
  emailContent: string | null;
  targetAudience: string | null;
  sequence: SequenceStep[] | null;
  sent: number;
  clicked: number;
  replied: number;
  bounced: number;
  /** Every lead on the campaign; only the campaigns list returns it. */
  leadsAdded?: number;
  agentId: string | null;
  listId: number | null;
  createdAt: Date;
  updatedAt: Date;
  lastViewedAt: Date | null;
  // Phase 2 — auto-prospecting
  icpDescription?: string | null;
  filterJson?: CampaignIcp | null;
  autoProspect?: boolean;
  autoSendEmails?: boolean;
  autoReply?: boolean;
  // Sending settings stored as real columns (not in targetAudience anymore).
  dailyLimit?: number;
  stopOnReply?: boolean;
  sendingDays?: string[];
  sendingStartTime?: string;
  sendingEndTime?: string;
  // Deliverability
  prioritiseNewLeads?: boolean;
  textOptOut?: boolean;
  // Sender pool: the mailboxes this campaign sends from. Each lead is
  // assigned to one at its first send and stays with it for every step.
  senderEmails?: string[];
  senderSummary?: SenderSummary;
};

/** What the pool did today and which leads are waiting, per campaign. */
export type SenderSummary = {
  senders: { email: string; sentToday: number; sentTotal: number }[];
  sentToday: number;
  held: { total: number; byReason: Record<string, number> };
};

export type PipelineFilters = {
  sicCodes?: string[];
  postTowns?: string[];
  counties?: string[];
  postCodePrefixes?: string[];
  countriesOfOrigin?: string[];
  incorporatedAfter?: string;
  incorporatedBefore?: string;
  statuses?: string[];
  companySizeRanges?: string[];
  roleKeywords?: string[];
  excludeAddressLines?: string[];
};

/**
 * Wizard-legible ICP shape — matches Phase1BusinessAnalysis fields.
 * Saved alongside the resolved PipelineFilters so edits round-trip.
 */
export type HumanIcp = {
  title?: string;
  seniority?: string;
  industry?: string;
  companySize?: string;
  geography?: string;
  customFilters?: string[];
  description?: string;
};

/** Dual-shape blob stored on campaigns.filter_json. */
export type CampaignIcp = {
  human: HumanIcp;
  resolved: PipelineFilters | null;
  resolvedAt: string | null;
  rationale: string | null;
  estimatedReach: number | null;
};

export type CampaignLead = {
  /** campaign_contacts row id: the key for lead-scoped routes under /campaigns/:id/leads */
  id: number;
  /** contacts row id: the key for /api/contacts routes such as verify */
  contactId?: number;
  campaignId: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  company: string | null;
  companyWebsite: string | null;
  linkedinUrl: string | null;
  location: string | null;
  enrichmentStatus: string | null;
  enrichmentConfidence?: number | null;
  emailStatus?: string | null;
  emailCheckedAt?: string | null;
  emailCheckStatus?: string | null;
  status: string;
  createdAt: Date;
  updatedAt: Date;
};

export type SequenceStep = {
  id: string;
  type: 'email';
  delay?: number;
  delayUnit?: 'minutes' | 'hours' | 'days';
  subject?: string;
  content?: string;
  aiContent?: string;  // deprecated — kept for backward compat with existing data
  aiMode?: boolean;    // deprecated — AI instructions now detected inline via {instructions} in content
  senderEmail?: string;
};

export type CreateCampaignData = {
  name: string;
  emailSubject?: string;
  emailContent?: string;
  targetAudience?: string;
};

export type UpdateCampaignNameData = {
  name: string;
};

export type UpdateCampaignSequenceData = {
  sequence: SequenceStep[];
};

export type UpdateCampaignSettingsData = {
  emailSubject?: string;
  emailContent?: string;
  targetAudience?: string;
  dailyLimit?: number;
  stopOnReply?: boolean;
  senderEmails?: string[];
};

export type UpdateCampaignStatusData = {
  status: 'draft' | 'active' | 'paused' | 'completed';
};

class CampaignService extends ApiService {
  private campaignsUrl = '/api/campaigns';

  constructor() {
    super();
  }

  async getCampaigns(): Promise<Campaign[]> {
    return this.get<Campaign[]>(this.campaignsUrl);
  }

  async getCampaign(id: string): Promise<Campaign> {
    return this.get<Campaign>(`${this.campaignsUrl}/${id}`);
  }

  async createCampaign(data: CreateCampaignData): Promise<Campaign> {
    return this.post<Campaign>(this.campaignsUrl, data);
  }

  async updateCampaignName(id: string, data: UpdateCampaignNameData): Promise<Campaign> {
    return this.patch<Campaign>(`${this.campaignsUrl}/${id}/name`, data);
  }

  async updateCampaignSequence(id: string, data: UpdateCampaignSequenceData): Promise<Campaign> {
    return this.patch<Campaign>(`${this.campaignsUrl}/${id}/sequence`, data);
  }

  async updateCampaignSettings(id: string, data: UpdateCampaignSettingsData): Promise<Campaign> {
    return this.patch<Campaign>(`${this.campaignsUrl}/${id}/settings`, data);
  }

  async updateCampaignStatus(id: string, data: UpdateCampaignStatusData): Promise<Campaign> {
    return this.patch<Campaign>(`${this.campaignsUrl}/${id}/status`, data);
  }

  async deleteCampaign(id: string): Promise<void> {
    return this.delete<void>(`${this.campaignsUrl}/${id}`);
  }

  async getCampaignLeads(id: string): Promise<CampaignLead[]> {
    return this.get<CampaignLead[]>(`${this.campaignsUrl}/${id}/leads`);
  }

  async addCampaignLeads(id: string, emails: string[]): Promise<CampaignLead[]> {
    return this.post<CampaignLead[]>(`${this.campaignsUrl}/${id}/leads`, { emails });
  }

  async deleteCampaignLead(id: string, leadId: string): Promise<void> {
    return this.delete<void>(`${this.campaignsUrl}/${id}/leads/${leadId}`);
  }

  async markCampaignAsViewed(id: string): Promise<{ success: boolean; lastViewedAt: Date }> {
    return this.patch<{ success: boolean; lastViewedAt: Date }>(`${this.campaignsUrl}/${id}/viewed`, {});
  }
}

export const campaignService = new CampaignService();

